import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { API_BASE_URL } from '../../core/config/api-config';
import {
  AddTicketMessagesResult,
  CreateTicketCustomFieldDefinitionRequest,
  CreateTicketResult,
  TicketBoardPage,
  TicketCustomFieldDefinition,
  TicketDetail,
  TicketReportingSummary,
  TicketSearchFilter,
  TicketSearchResult,
  TicketType,
  UpdateTicketCustomFieldDefinitionRequest,
} from './ticketing.models';

/** HTTP client for FiveWORX.Communication.API.Controllers.TicketsController. */
@Injectable({ providedIn: 'root' })
export class TicketingApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(API_BASE_URL);

  private get ticketsUrl(): string {
    return `${this.baseUrl}/v1/tickets`;
  }

  getTicketTypes(includeInactive = false): Observable<TicketType[]> {
    return this.http.get<TicketType[]>(`${this.ticketsUrl}/types`, { params: { includeInactive } });
  }

  createTicketType(name: string, description: string | null): Observable<{ success: boolean; ticketTypeId: string | null; failureReason: string | null }> {
    return this.http.post<{ success: boolean; ticketTypeId: string | null; failureReason: string | null }>(
      `${this.ticketsUrl}/types`,
      { name, description },
    );
  }

  updateTicketType(ticketTypeId: string, name: string, description: string | null): Observable<void> {
    return this.http.put<void>(`${this.ticketsUrl}/types/${ticketTypeId}`, { name, description });
  }

  setTicketTypeActive(ticketTypeId: string, isActive: boolean): Observable<void> {
    return this.http.post<void>(`${this.ticketsUrl}/types/${ticketTypeId}/active`, { isActive });
  }

  getCustomFieldDefinitions(ticketTypeId: string, activeOnly = false): Observable<TicketCustomFieldDefinition[]> {
    return this.http.get<TicketCustomFieldDefinition[]>(
      `${this.ticketsUrl}/types/${ticketTypeId}/custom-field-definitions`,
      { params: { activeOnly } },
    );
  }

  createCustomFieldDefinition(
    ticketTypeId: string,
    request: CreateTicketCustomFieldDefinitionRequest,
  ): Observable<{ success: boolean; definitionId: string | null; failureReason: string | null }> {
    return this.http.post<{ success: boolean; definitionId: string | null; failureReason: string | null }>(
      `${this.ticketsUrl}/types/${ticketTypeId}/custom-field-definitions`,
      request,
    );
  }

  updateCustomFieldDefinition(
    ticketTypeId: string,
    definitionId: string,
    request: UpdateTicketCustomFieldDefinitionRequest,
  ): Observable<void> {
    return this.http.put<void>(`${this.ticketsUrl}/types/${ticketTypeId}/custom-field-definitions/${definitionId}`, request);
  }

  setCustomFieldDefinitionActive(ticketTypeId: string, definitionId: string, isActive: boolean): Observable<void> {
    return this.http.post<void>(
      `${this.ticketsUrl}/types/${ticketTypeId}/custom-field-definitions/${definitionId}/active`,
      { isActive },
    );
  }

  getBoard(status?: string, page = 1, pageSize = 25): Observable<TicketBoardPage> {
    const params: Record<string, string | number> = { page, pageSize };

    if (status) {
      params['status'] = status;
    }

    return this.http.get<TicketBoardPage>(this.ticketsUrl, { params });
  }

  /** GET /tickets/search -- the flexible filter set every named Inbox view (My/Team/Queue/
   * Waiting/Escalated/Unassigned/Recent) is built from; see InboxViewsService. */
  search(filter: TicketSearchFilter): Observable<TicketSearchResult> {
    const params: Record<string, string | number | boolean> = {};

    for (const [key, value] of Object.entries(filter)) {
      if (value !== undefined && value !== null && value !== '') {
        params[key] = value as string | number | boolean;
      }
    }

    return this.http.get<TicketSearchResult>(`${this.ticketsUrl}/search`, { params });
  }

  getTicket(ticketId: string): Observable<TicketDetail> {
    return this.http.get<TicketDetail>(`${this.ticketsUrl}/${ticketId}`);
  }

  createFromConversation(
    conversationId: string,
    ticketTypeId: string,
    subject: string,
    description?: string,
    customFieldValues?: Record<string, string | null>,
  ): Observable<CreateTicketResult> {
    return this.http.post<CreateTicketResult>(this.ticketsUrl, { conversationId, ticketTypeId, subject, description, customFieldValues });
  }

  changeStatus(ticketId: string, newStatus: string): Observable<void> {
    return this.http.post<void>(`${this.ticketsUrl}/${ticketId}/status`, { newStatus });
  }

  assign(ticketId: string, assigneeUserId: string | null): Observable<void> {
    return this.http.post<void>(`${this.ticketsUrl}/${ticketId}/assign`, { assigneeUserId });
  }

  assignToQueue(ticketId: string, queueId: string | null): Observable<void> {
    return this.http.post<void>(`${this.ticketsUrl}/${ticketId}/queue`, { queueId });
  }

  assignToDepartment(ticketId: string, departmentId: string | null): Observable<void> {
    return this.http.post<void>(`${this.ticketsUrl}/${ticketId}/department`, { departmentId });
  }

  transfer(ticketId: string, newQueueId: string | null, newDepartmentId: string | null, newAssigneeUserId: string | null): Observable<void> {
    return this.http.post<void>(`${this.ticketsUrl}/${ticketId}/transfer`, { newQueueId, newDepartmentId, newAssigneeUserId });
  }

  resolve(ticketId: string, resolutionSummary?: string): Observable<void> {
    return this.http.post<void>(`${this.ticketsUrl}/${ticketId}/resolve`, { resolutionSummary });
  }

  close(ticketId: string): Observable<void> {
    return this.http.post<void>(`${this.ticketsUrl}/${ticketId}/close`, {});
  }

  cancel(ticketId: string, reason?: string): Observable<void> {
    return this.http.post<void>(`${this.ticketsUrl}/${ticketId}/cancel`, { reason });
  }

  reopen(ticketId: string): Observable<void> {
    return this.http.post<void>(`${this.ticketsUrl}/${ticketId}/reopen`, {});
  }

  archive(ticketId: string): Observable<void> {
    return this.http.post<void>(`${this.ticketsUrl}/${ticketId}/archive`, {});
  }

  /** Both endpoints return 204 No Content on success and 400 (with an ActionResult body) on
   * failure -- success never carries a body, so Observable<void> is correct here; a failed
   * call surfaces through the Observable's error channel instead. */
  escalate(ticketId: string, newPriority: string): Observable<void> {
    return this.http.post<void>(`${this.ticketsUrl}/${ticketId}/escalate`, { newPriority });
  }

  setPriority(ticketId: string, priority: string): Observable<void> {
    return this.http.post<void>(`${this.ticketsUrl}/${ticketId}/priority`, { priority });
  }

  setCategory(ticketId: string, categoryId: string | null, subCategoryId: string | null): Observable<void> {
    return this.http.post<void>(`${this.ticketsUrl}/${ticketId}/category`, { categoryId, subCategoryId });
  }

  addTag(ticketId: string, tag: string): Observable<void> {
    return this.http.post<void>(`${this.ticketsUrl}/${ticketId}/tags`, { tag });
  }

  removeTag(ticketId: string, tag: string): Observable<void> {
    return this.http.delete<void>(`${this.ticketsUrl}/${ticketId}/tags/${encodeURIComponent(tag)}`);
  }

  setCustomField(ticketId: string, fieldKey: string, fieldValue: string | null): Observable<void> {
    return this.http.post<void>(`${this.ticketsUrl}/${ticketId}/custom-fields`, { fieldKey, fieldValue });
  }

  getReportingSummary(fromUtc?: string, toUtc?: string): Observable<TicketReportingSummary> {
    const params: Record<string, string> = {};

    if (fromUtc) {
      params['fromUtc'] = fromUtc;
    }

    if (toUtc) {
      params['toUtc'] = toUtc;
    }

    return this.http.get<TicketReportingSummary>(`${this.ticketsUrl}/reports/summary`, { params });
  }

  addNote(ticketId: string, content: string): Observable<{ success: boolean; noteId: string | null }> {
    return this.http.post<{ success: boolean; noteId: string | null }>(`${this.ticketsUrl}/${ticketId}/notes`, { content });
  }

  addAttachment(ticketId: string, file: File): Observable<{ success: boolean; attachmentId: string | null }> {
    const formData = new FormData();
    formData.append('file', file);

    return this.http.post<{ success: boolean; attachmentId: string | null }>(`${this.ticketsUrl}/${ticketId}/attachments`, formData);
  }

  addMessages(ticketId: string, messageIds: string[]): Observable<AddTicketMessagesResult> {
    return this.http.post<AddTicketMessagesResult>(`${this.ticketsUrl}/${ticketId}/messages`, { messageIds });
  }
}
